import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ImpactItem {
  icon: string;
  value: number;
  suffix: string;
  label: string;
  image: string;
  color: string;
}

function AnimatedCounter({ target, suffix, isVisible }: { target: number; suffix: string; isVisible: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 2000;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, target]);

  const formatted = count >= 1000 ? count.toLocaleString('fr-FR') : count;

  return (
    <span>
      {formatted}
      {suffix}
    </span>
  );
}

export default function EcoImpact() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const impactData: ImpactItem[] = [
    {
      icon: 'ri-suitcase-3-line',
      value: 20000,
      suffix: '+',
      label: t('ecoImpact.metric1'),
      image: 'https://public.readdy.ai/ai/img_res/aa1f4daadf138d31b0253f5608a90dd4.jpg',
      color: '#FF6B6B',
    },
    {
      icon: 'ri-delete-bin-line',
      value: 12,
      suffix: ' t',
      label: t('ecoImpact.metric2'),
      image: 'https://readdy.ai/api/search-image?query=Cute%20minimal%20flat%20illustration%20of%20a%20trash%20bin%20with%20a%20red%20cross%20mark%20showing%20waste%20reduction%20concept%2C%20soft%20pastel%20blue%20circular%20background%2C%20kawaii%20style%20simple%20clean%20vector%20art%2C%20eco%20friendly%20zero%20waste%20concept%2C%20light%20teal%20and%20green%20accents%2C%20adorable%20cartoon%20style%20with%20soft%20shadows&width=280&height=280&seq=eco-impact-waste-02&orientation=squarish',
      color: '#4ECDC4',
    },
    {
      icon: 'ri-leaf-line',
      value: 8500,
      suffix: ' kg',
      label: t('ecoImpact.metric3'),
      image: 'https://readdy.ai/api/search-image?query=Cute%20minimal%20flat%20illustration%20of%20a%20green%20leaf%20with%20CO2%20cloud%20being%20reduced%2C%20soft%20pastel%20blue%20circular%20background%2C%20kawaii%20style%20simple%20clean%20vector%20art%2C%20carbon%20footprint%20reduction%20concept%2C%20light%20green%20and%20teal%20accents%2C%20adorable%20cartoon%20style%20with%20soft%20shadows&width=280&height=280&seq=eco-impact-co2-03&orientation=squarish',
      color: '#44A08D',
    },
    {
      icon: 'ri-recycle-line',
      value: 95,
      suffix: '%',
      label: t('ecoImpact.metric4'),
      image: 'https://readdy.ai/api/search-image?query=Cute%20minimal%20flat%20illustration%20of%20recycling%20arrows%20symbol%20with%20small%20gear%20parts%20inside%2C%20soft%20pastel%20blue%20circular%20background%2C%20kawaii%20style%20simple%20clean%20vector%20art%2C%20circular%20economy%20reuse%20concept%2C%20light%20coral%20and%20teal%20accents%2C%20adorable%20cartoon%20style%20with%20soft%20shadows&width=280&height=280&seq=eco-impact-recycle-04&orientation=squarish',
      color: '#F59E0B',
    },
    {
      icon: 'ri-hand-heart-line',
      value: 19200,
      suffix: '+',
      label: t('ecoImpact.metric5'),
      image: 'https://readdy.ai/api/search-image?query=Cute%20minimal%20flat%20illustration%20of%20a%20happy%20smiling%20heart%20with%20small%20sparkles%20around%20it%20showing%20customer%20satisfaction%2C%20soft%20pastel%20blue%20circular%20background%2C%20kawaii%20style%20simple%20clean%20vector%20art%2C%20happy%20customer%20love%20concept%2C%20light%20pink%20and%20coral%20accents%2C%20adorable%20cartoon%20style%20with%20soft%20shadows&width=280&height=280&seq=eco-impact-happy-05&orientation=squarish',
      color: '#A855F7',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-[#FAFCFC] relative overflow-hidden">
      {/* Subtle background shapes */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-[#4ECDC4]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-72 h-72 bg-[#FF6B6B]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#4ECDC4]/10 rounded-full text-[#4ECDC4] text-sm font-semibold mb-5">
            <i className="ri-earth-line"></i>
            {t('ecoImpact.badge')}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t('ecoImpact.title')}{' '}
            <span className="bg-gradient-to-r from-[#4ECDC4] to-[#44A08D] bg-clip-text text-transparent">
              {t('ecoImpact.titleHighlight')}
            </span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-[15px]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('ecoImpact.subtitle')}
          </p>
        </div>

        {/* Impact Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
          {impactData.map((item, index) => (
            <div
              key={index}
              className="group flex flex-col items-center text-center"
              style={{ animationDelay: `${index * 120}ms` }}
            >
              {/* Illustration circle */}
              <div className="relative mb-5">
                <div
                  className="w-40 h-40 rounded-full overflow-hidden bg-gradient-to-b from-[#E8F4F8] to-[#D4EEF6] flex items-center justify-center group-hover:scale-105 transition-transform duration-500 shadow-sm"
                >
                  <img
                    src={item.image}
                    alt={item.label}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Small floating accent */}
                <div
                  className="absolute -top-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center shadow-md"
                  style={{ backgroundColor: item.color }}
                >
                  <i className={`${item.icon} text-white text-sm`}></i>
                </div>
              </div>

              {/* Counter */}
              <div
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-1"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <AnimatedCounter target={item.value} suffix={item.suffix} isVisible={isVisible} />
              </div>

              {/* Label */}
              <p className="text-gray-500 text-sm leading-snug max-w-[160px]">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-16 flex justify-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4ECDC4] to-[#44A08D] flex items-center justify-center flex-shrink-0">
              <i className="ri-seedling-line text-white text-lg"></i>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <strong className="text-gray-900">{t('ecoImpact.noteTitle')}</strong> {t('ecoImpact.noteText')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
